import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { PermissionService } from '../services/permission.service';
import { MessageService } from 'primeng/api';
import { PersistentErrorService } from '../services/persistent-error.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate, CanActivateChild {

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private messageService: MessageService,
    private persistentErrorService: PersistentErrorService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.checkPermissions(route, state);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.checkPermissions(childRoute, state);
  }

  private checkPermissions(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    // Get permission requirements from route data
    const requiredPermissions = route.data['permissions'] as string[];
    const requiredRoles = route.data['roles'] as string[];
    const requireAll = route.data['requireAll'] as boolean || false;
    const allowedRoleLevel = route.data['roleLevel'] as string;

    // If no permissions specified, allow access
    if (!requiredPermissions && !requiredRoles && !allowedRoleLevel) {
      return true;
    }

    // Check if user permissions are loaded
    const userPermissions = this.permissionService.getUserPermissions();
    if (!userPermissions || !userPermissions.permissions) {
      console.log('PermissionGuard: User permissions not loaded yet, allowing access temporarily');
      return true;
    }

    // SUPER_DEV_ADMIN bypasses all permission checks
    if (userPermissions?.role?.identification === 'SUPER_DEV_ADMIN') {
      console.log('PermissionGuard: SUPER_DEV_ADMIN bypassing all permission checks');
      return true;
    }

    // Check role-based access
    if (requiredRoles && requiredRoles.length > 0) {
      if (!this.permissionService.hasAnyRole(requiredRoles)) {
        this.handleAccessDenied('You do not have the required role to access this page.');
        return false;
      }
    }

    // Check role level access
    if (allowedRoleLevel) {
      const userRoleLevel = this.permissionService.getUserRoleLevel();
      if (userRoleLevel !== allowedRoleLevel) {
        this.handleAccessDenied('Your role level does not have access to this page.');
        return false;
      }
    }

    // Check permission-based access
    if (requiredPermissions && requiredPermissions.length > 0) {
      let hasAccess = false;

      if (requireAll) {
        hasAccess = this.permissionService.hasAllPermissions(requiredPermissions);
      } else {
        hasAccess = this.permissionService.hasAnyPermission(requiredPermissions);
      }

      if (!hasAccess) {
        this.handleAccessDenied('You do not have the required permissions to access this page.');
        return false;
      }
    }

    return true;
  }

  private handleAccessDenied(message: string): void {
    // Use persistent error service for permission errors that survive route changes
    this.persistentErrorService.addPermissionError(message);
    
    // Redirect to unauthorized page instead of logging out
    this.router.navigate(['/admin/unauthorized']);
  }
}

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate, CanActivateChild {

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private messageService: MessageService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    return this.checkRole(route);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    return this.checkRole(childRoute);
  }

  private checkRole(route: ActivatedRouteSnapshot): boolean {
    const allowedRoles = route.data['allowedRoles'] as string[];
    
    if (!allowedRoles || allowedRoles.length === 0) {
      return true;
    }



    if (!this.permissionService.hasAnyRole(allowedRoles)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Access Denied',
        detail: 'You do not have the required role to access this page.',
        sticky: true,
        closable: true
      });
      
      this.router.navigate(['/dashboard']);
      return false;
    }

    return true;
  }
}
